import {
  FirstLastInstrument,
  OpmFirstLastWaterfallDictionaryDto,
  OpmWaterfallConditionDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { FC } from 'react';
import { TrancheLevelRow } from './row-components/TrancheLevelRow';
import { TrancheLevelToeConditionRows } from './row-components/TrancheLevelToeConditionRows';

interface InstrumentExitDistributionsHurdleDetailsProps {
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  waterfallIterations: number;
  instrumentHasTranches: boolean;
  instrumentKey: string;
  instrument: FirstLastInstrument;
  currentTrancheIndex: number;
}

const InstrumentExitDistributionsHurdleDetails: FC<
  InstrumentExitDistributionsHurdleDetailsProps
> = ({
  opmWaterfall,
  selectedYear,
  waterfallIterations,
  instrumentHasTranches,
  instrumentKey,
  instrument,
  currentTrancheIndex,
}): JSX.Element => {
  const firstSimulation = Object.keys(opmWaterfall[selectedYear].first)[0];

  const trancheKeys = Object.keys(instrument.tranches);
  const currentTrancheKey = trancheKeys[currentTrancheIndex];
  const currentTranche = instrument.tranches[currentTrancheKey];
  const currentTrancheConditions = currentTranche.conditions;
  const oeConditions = currentTranche.ordinaryEquityMetrics?.conditions ?? [];

  const hasIrrOrMomHurdle = (conditions: OpmWaterfallConditionDto[]) =>
    conditions.some(
      (condition) =>
        condition.metric === 'InstitutionalIRR' || condition.metric === 'InstitutionalMoM'
    );

  function hasMultipleInstitutionalConditions(instrument: FirstLastInstrument): boolean {
    const trancheKeys = Object.keys(instrument.tranches);
    return trancheKeys.some((key) => {
      const conditions = instrument.tranches[key].conditions;
      const institutionalCount = conditions.filter(
        (condition) =>
          condition.metric === 'InstitutionalIRR' || condition.metric === 'InstitutionalMoM'
      ).length;
      return institutionalCount > 1;
    });
  }

  const isToeOnlyTranche = oeConditions.length > 0 && !hasIrrOrMomHurdle(currentTrancheConditions);

  return (
    <>
      {hasMultipleInstitutionalConditions(instrument) && (
        <TrancheLevelRow
          label="_InstitutionHurdleActive"
          valueKey="activeHurdle"
          opmWaterfall={opmWaterfall}
          selectedYear={selectedYear}
          firstSimulation={firstSimulation}
          waterfallIterations={waterfallIterations}
          instrumentKey={instrumentKey}
          currentTrancheIndex={currentTrancheIndex}
        />
      )}
      {hasIrrOrMomHurdle(currentTrancheConditions) && (
        <TrancheLevelRow
          label="_LessSponsorDebtColon"
          valueKey="lessSponsorDebt"
          opmWaterfall={opmWaterfall}
          selectedYear={selectedYear}
          firstSimulation={firstSimulation}
          waterfallIterations={waterfallIterations}
          instrumentKey={instrumentKey}
          currentTrancheIndex={currentTrancheIndex}
        />
      )}
      {hasIrrOrMomHurdle(currentTrancheConditions) && (
        <TrancheLevelRow
          label="_HurdleRemaining"
          valueKey="hurdleRemaining"
          opmWaterfall={opmWaterfall}
          selectedYear={selectedYear}
          firstSimulation={firstSimulation}
          waterfallIterations={waterfallIterations}
          instrumentKey={instrumentKey}
          currentTrancheIndex={currentTrancheIndex}
        />
      )}
      {hasIrrOrMomHurdle(currentTrancheConditions) && (
        <TrancheLevelRow
          label="_GrossUpPercent"
          valueKey="grossUp"
          opmWaterfall={opmWaterfall}
          selectedYear={selectedYear}
          firstSimulation={firstSimulation}
          waterfallIterations={waterfallIterations}
          instrumentKey={instrumentKey}
          isPercentage
          currentTrancheIndex={currentTrancheIndex}
        />
      )}
      {isToeOnlyTranche && (
        <TrancheLevelToeConditionRows
          conditions={oeConditions}
          opmWaterfall={opmWaterfall}
          selectedYear={selectedYear}
          waterfallIterations={waterfallIterations}
          instrumentKey={instrumentKey}
          currentTrancheIndex={currentTrancheIndex}
        />
      )}
      <TrancheLevelRow
        label="_EffectiveHurdle"
        valueKey="ordinaryEquityHurdle"
        opmWaterfall={opmWaterfall}
        selectedYear={selectedYear}
        firstSimulation={firstSimulation}
        waterfallIterations={waterfallIterations}
        instrumentKey={instrumentKey}
        currentTrancheIndex={currentTrancheIndex}
      />
      {instrumentHasTranches && (
        <>
          <TrancheLevelRow
            label="_IsHurdleMet"
            valueKey="isHurdleMet"
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            firstSimulation={firstSimulation}
            waterfallIterations={waterfallIterations}
            instrumentKey={instrumentKey}
            upper
            currentTrancheIndex={currentTrancheIndex}
          />
          <TrancheLevelRow
            label="_OrdinaryEquityPostHurdle"
            valueKey="ordinaryEquityPostHurdle"
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            firstSimulation={firstSimulation}
            waterfallIterations={waterfallIterations}
            instrumentKey={instrumentKey}
            currentTrancheIndex={currentTrancheIndex}
          />
          <TrancheLevelRow
            label="_PcParticipationTitle"
            valueKey="trancheParticipationPc"
            opmWaterfall={opmWaterfall}
            selectedYear={selectedYear}
            firstSimulation={firstSimulation}
            waterfallIterations={waterfallIterations}
            instrumentKey={instrumentKey}
            isPercentage
            currentTrancheIndex={currentTrancheIndex}
          />
        </>
      )}
    </>
  );
};

export default InstrumentExitDistributionsHurdleDetails;
